import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import RollingNumber from "../components/RollingNumber";
import CountdownTimer from "../components/CountdownTimer";
import HowToPlay from "../components/HowToPlay";
import WhatsAppShare from "../components/WhatsAppShare";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Slot machine timing: reels start spinning this many seconds before the next draw.
const SPIN_LEAD_SEC = 60;
// Staggered stop delays (ms) after result arrives — Jackpot Royal, Golden, Aakhri Dao.
const STOP_STAGGER_MS = [0, 600, 1200];

function todayIST() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function formatDateDMY(isoDate) {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return `${d}-${m}-${y}`;
}

function formatSlotDisplay(slot) {
  const [hStr, mStr] = slot.split(":");
  let h = parseInt(hStr, 10);
  const suffix = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${String(h).padStart(2, "0")}:${mStr} ${suffix}`;
}

// Given an upcoming slot ("HH:MM"), return the slot that was drawn immediately
// before it. Returns null when the day has not yet reached its first draw
// (i.e. upcoming is 09:00, so no previous slot exists for today).
// Used to determine whether the "most recent passed slot" has a published
// result — if not, reels should be spinning.
function prevSlot(slot) {
  if (!slot) return null;
  const [h, m] = slot.split(":").map(Number);
  const totalMin = h * 60 + m - 15;
  if (totalMin < 9 * 60) return null;
  const ph = Math.floor(totalMin / 60);
  const pm = totalMin % 60;
  return `${String(ph).padStart(2, "0")}:${String(pm).padStart(2, "0")}`;
}

// Returns the "HH:MM" of the slot that has most recently been drawn based on
// the current IST wall-clock. Returns null when before 09:00 IST (no slot yet
// today). Returns "21:00" for any time >= 21:00 (day's last slot).
// Used by the staggered-stop useEffect to decide whether a newly-arrived
// result corresponds to the current expected slot (→ stop reels & reveal) or
// to an older slot (→ keep reels spinning while awaiting the current one).
function mostRecentPassedSlotNow() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(new Date());
  const v = (t) => parseInt(parts.find((p) => p.type === t).value, 10);
  const totalSec = v("hour") * 3600 + v("minute") * 60 + v("second");
  const startSec = 9 * 3600;
  const endSec = 21 * 3600;
  if (totalSec < startSec) return null;
  if (totalSec >= endSec) return "21:00";
  const slotIndex = Math.floor((totalSec - startSec) / (15 * 60));
  const slotMin = slotIndex * 15;
  const bh = 9 + Math.floor(slotMin / 60);
  const bm = slotMin % 60;
  return `${String(bh).padStart(2, "0")}:${String(bm).padStart(2, "0")}`;
}

export default function Home() {
  const [selectedDate, setSelectedDate] = useState(todayIST());
  const [queryDate, setQueryDate] = useState(todayIST());
  const [rows, setRows] = useState([]);
  const [latest, setLatest] = useState({ jackpot_regel: null, golden: null, aakhri_dao: null });
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async (date) => {
    setLoading(true);
    try {
      const [rs, lt] = await Promise.all([
        axios.get(`${API}/results`, { params: { date } }),
        axios.get(`${API}/latest`, { params: { date } }),
      ]);
      setRows(rs.data || []);
      setLatest(lt.data || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // ---- Slot machine spinning orchestration ----
  // spinTiles[i] = true means the i-th reel (0=Jackpot Royal, 1=Golden, 2=Aakhri Dao)
  // is currently spinning. Managed by Home so we can stagger the stop.
  const [spinTiles, setSpinTiles] = useState([false, false, false]);
  const spinTimersRef = useRef([]);
  const prevLatestRef = useRef({ jackpot_regel: null, golden: null, aakhri_dao: null });

  const clearStopTimers = () => {
    spinTimersRef.current.forEach(clearTimeout);
    spinTimersRef.current = [];
  };

  const startSpinning = useCallback(() => {
    clearStopTimers();
    setSpinTiles([true, true, true]);
  }, []);

  const stopSpinningStaggered = useCallback(() => {
    clearStopTimers();
    spinTimersRef.current = STOP_STAGGER_MS.map((delay, idx) =>
      setTimeout(() => {
        setSpinTiles((prev) => {
          const next = [...prev];
          next[idx] = false;
          return next;
        });
      }, delay)
    );
  }, []);

  // Called every second by CountdownTimer. Idempotent — safely starts spinning
  // whenever we are within 60s of a draw AND the result for that upcoming slot
  // hasn't arrived yet. Tracks the "current spin slot" so we spin exactly ONCE
  // per draw window; when the SSE result stops the reels, we won't restart
  // during the same window.
  const isTodayViewRef = useRef(true);
  const currentSpinSlotRef = useRef(null); // upcoming slot we're currently spinning FOR (e.g. "09:45")
  const latestRef = useRef({ jackpot_regel: null, golden: null, aakhri_dao: null, time_slot: null });
  useEffect(() => { latestRef.current = latest; }, [latest]);

  const onCountdownTick = useCallback(
    (remainingSec, status, upcomingSlot) => {
      if (!isTodayViewRef.current) return;
      // Outside the live window (before 09:00 or after 21:00), reels stay static.
      if (status !== "live") return;

      const cur = latestRef.current || {};
      const latestSlot = cur.time_slot || "";

      // The most recent slot that has already been drawn today.
      // If null → we are before 09:00's first draw, so nothing is "missing".
      const passedSlot = prevSlot(upcomingSlot);

      // If the currently-shown latest result IS the upcoming slot's result
      // (rare: manual pre-publish), don't spin.
      const upcomingAlreadyPublished =
        latestSlot === upcomingSlot &&
        (cur.jackpot_regel || cur.golden || cur.aakhri_dao);
      if (upcomingAlreadyPublished) {
        currentSpinSlotRef.current = null;
        return;
      }

      // Two independent reasons to spin:
      //  (a) The most-recent-passed slot has NO published result yet — reels
      //      must keep spinning continuously (survives page refresh; state is
      //      derived purely from current IST time + latest fetched data).
      //  (b) We are within the 60-second pre-draw window for the next slot —
      //      the classic "reels warming up before the draw" animation.
      const missingLatestResult =
        passedSlot !== null && (!latestSlot || latestSlot < passedSlot);
      const inPreDrawWindow = remainingSec <= SPIN_LEAD_SEC && remainingSec > 0;

      if (!missingLatestResult && !inPreDrawWindow) return;

      // Track the current spin slot so we spin exactly once per draw window
      // and don't restart after the SSE-driven staggered stop.
      if (currentSpinSlotRef.current !== upcomingSlot) {
        currentSpinSlotRef.current = upcomingSlot;
      }
      // Idempotent: only start a fresh spin when reels are FULLY stopped.
      // If any reel is still spinning (either the ongoing spin or a staggered
      // stop in progress), leave the state alone — this preserves the smooth
      // reveal animation without re-igniting mid-stagger.
      setSpinTiles((prev) =>
        prev[0] || prev[1] || prev[2] ? prev : [true, true, true]
      );
    },
    []
  );

  // ---- E2E test hooks: expose safe helpers on `window` so automated tests
  // can trigger the spin window and verify staggered stop without waiting up
  // to 15 minutes for a natural draw. Purely UI-side; never touches backend. ----
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__lw5g = {
      // Force all three reels to start spinning immediately.
      forceStartSpin: () => setSpinTiles([true, true, true]),
      // Force staggered stop (identical to what happens on real SSE result).
      forceStaggeredStop: () => stopSpinningStaggered(),
      // Read current spin state.
      getSpinTiles: () => [...spinTilesRef.current],
    };
    return () => { try { delete window.__lw5g; } catch (e) { console.debug("__lw5g cleanup:", e); } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopSpinningStaggered]);
  // Keep a ref of current spin tiles so the getter above returns fresh data.
  const spinTilesRef = useRef([false, false, false]);
  useEffect(() => { spinTilesRef.current = spinTiles; }, [spinTiles]);

  // Apply an incoming SSE event to local state (rows + latest) idempotently.
  // No duplicate rows are ever created — we always merge by (date + time_slot).
  const applyResultEvent = useCallback((evt) => {
    if (!evt || !evt.date || !evt.time_slot) return;
    // Only reflect events for the currently displayed date
    if (evt.date !== queryDate) return;

    if (evt.type === "delete") {
      setRows((prev) =>
        prev.map((r) =>
          r.time_slot === evt.time_slot
            ? { ...r, jackpot_regel: null, golden: null, aakhri_dao: null }
            : r
        )
      );
      // If the deleted row was the latest, we let the auto-poll refresh /latest.
      return;
    }

    // upsert
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.time_slot === evt.time_slot);
      const merged = {
        time_slot: evt.time_slot,
        jackpot_regel: evt.jackpot_regel ?? null,
        golden: evt.golden ?? null,
        aakhri_dao: evt.aakhri_dao ?? null,
      };
      if (idx === -1) return [...prev, merged];
      const next = prev.slice();
      next[idx] = { ...next[idx], ...merged };
      return next;
    });

    // Only update the top-tile "latest" if this event is at least as recent as
    // the currently shown latest slot (compare time_slot lexicographically works
    // since slots are HH:MM zero-padded).
    setLatest((prev) => {
      const prevSlot = prev?.time_slot || "";
      if (evt.time_slot >= prevSlot) {
        return {
          date: evt.date,
          time_slot: evt.time_slot,
          jackpot_regel: evt.jackpot_regel ?? null,
          golden: evt.golden ?? null,
          aakhri_dao: evt.aakhri_dao ?? null,
        };
      }
      return prev;
    });
  }, [queryDate]);

  // ---- Real-time SSE subscription (with graceful reconnect + polling fallback) ----
  useEffect(() => {
    let es = null;
    let closed = false;
    let fallbackTimer = null;
    let consecutiveFailures = 0;

    const startFallbackPolling = () => {
      if (fallbackTimer) return;
      // Lightweight 5s polling ONLY as fallback (SSE cannot connect).
      fallbackTimer = setInterval(() => {
        if (queryDate === todayIST()) fetchAll(queryDate);
      }, 5000);
    };
    const stopFallbackPolling = () => {
      if (fallbackTimer) { clearInterval(fallbackTimer); fallbackTimer = null; }
    };

    const connect = () => {
      if (closed) return;
      try {
        es = new EventSource(`${API}/results/stream`);
        es.addEventListener("open", () => {
          consecutiveFailures = 0;
          stopFallbackPolling();
        });
        es.addEventListener("result", (msg) => {
          try {
            const payload = JSON.parse(msg.data);
            applyResultEvent(payload);
          } catch (e) { console.error("SSE payload parse error:", e); }
        });
        es.addEventListener("error", () => {
          // The browser's EventSource will auto-retry itself. If it repeatedly
          // fails, we escalate to polling as a safety net.
          consecutiveFailures += 1;
          if (consecutiveFailures >= 3) startFallbackPolling();
        });
      } catch (e) {
        // EventSource not supported → polling only
        console.error("EventSource unavailable, using fallback polling:", e);
        startFallbackPolling();
      }
    };

    connect();

    return () => {
      closed = true;
      if (es) es.close();
      stopFallbackPolling();
    };
  }, [applyResultEvent, fetchAll, queryDate]);

  useEffect(() => {
    fetchAll(queryDate);
    // Slow background reconciliation (60s) — covers the rare case where an SSE
    // event was missed while the tab was throttled/backgrounded.
    const interval = setInterval(() => {
      if (queryDate === todayIST()) fetchAll(queryDate);
    }, 60000);
    return () => clearInterval(interval);
  }, [queryDate, fetchAll]);

  const onSearch = () => {
    setQueryDate(selectedDate);
  };

  // Refresh promptly when countdown reaches the next slot
  const onDrawReached = useCallback(() => {
    if (queryDate === todayIST()) {
      // Small delay to give the backend a moment to persist the draw
      setTimeout(() => fetchAll(queryDate), 2500);
    }
  }, [queryDate, fetchAll]);

  const displayValue = (v) => (v && v !== "" ? v : "--");

  // Find newest slot in the current rows that actually has result data
  const latestSlot = useMemo(() => {
    for (let i = rows.length - 1; i >= 0; i--) {
      const r = rows[i];
      if (r.jackpot_regel || r.golden || r.aakhri_dao) return r.time_slot;
    }
    return null;
  }, [rows]);

  const isTodayView = queryDate === todayIST();

  // Keep the ref in sync so the tick callback doesn't need to re-subscribe.
  useEffect(() => {
    isTodayViewRef.current = isTodayView;
    if (!isTodayView) {
      // Viewing a past date: static values, never spin.
      clearStopTimers();
      setSpinTiles([false, false, false]);
    }
  }, [isTodayView]);

  // Cleanup any pending stop timers on unmount
  useEffect(() => () => clearStopTimers(), []);

  // Detect a real backend result change → trigger staggered stop of currently-spinning reels.
  useEffect(() => {
    const prev = prevLatestRef.current || {};
    const curr = latest || {};
    prevLatestRef.current = curr;

    if (!isTodayView) return;

    const changed =
      curr.jackpot_regel !== prev.jackpot_regel ||
      curr.golden !== prev.golden ||
      curr.aakhri_dao !== prev.aakhri_dao;
    const hasData = !!(curr.jackpot_regel || curr.golden || curr.aakhri_dao);

    if (!changed || !hasData) return;

    // Only stagger-stop when the newly-arrived result IS for the current
    // most-recent-passed slot. Otherwise (e.g. an initial fetch that returns
    // an OLDER slot's result while a newer slot has already passed with no
    // publish yet), keep reels spinning — they must resume automatically on
    // page refresh and continue until the correct result arrives.
    const passed = mostRecentPassedSlotNow();
    if (!passed || curr.time_slot !== passed) return;

    // Only stagger-stop if we WERE spinning; otherwise pure display update.
    setSpinTiles((cur) => {
      if (cur.some(Boolean)) stopSpinningStaggered();
      return cur;
    });
  }, [latest, isTodayView, stopSpinningStaggered]);

  return (
    <div className="result-shell" data-testid="home-page">
      <div className="brand-header" data-testid="brand-header">
        <img
          src="https://customer-assets-eiarnc6j.emergentagent.net/job_test-score-tracker-3/artifacts/sra67z89_file_000000008dfc820b890a19fd3f0d42fb.png"
          alt="LuckyWin5G"
          className="brand-logo"
          data-testid="brand-logo"
          width="146"
          height="144"
          fetchPriority="high"
          decoding="async"
        />
      </div>

      {/* Countdown timer — only for today's view */}
      {isTodayView && (
        <CountdownTimer onDrawReached={onDrawReached} onTick={onCountdownTick} />
      )}

      {/* Unified Casino Board: Top tiles + Chart title + Date + Table */}
      <div className="casino-board" data-testid="casino-board">
        {/* Latest 3 tiles strip */}
        <div className="latest-strip" data-testid="latest-strip">
          <div className="tile" data-testid="tile-jackpot">
            <div className="tile-label"><span className="crown">♛</span> Jackpot Royal</div>
            <div className="tile-value" data-testid="latest-jackpot-value">
              <RollingNumber
                value={latest?.jackpot_regel}
                isSpinning={spinTiles[0]}
                testid="reel-jackpot"
              />
            </div>
          </div>
          <div className="tile" data-testid="tile-golden">
            <div className="tile-label"><span className="crown">♛</span> Golden</div>
            <div className="tile-value" data-testid="latest-golden-value">
              <RollingNumber
                value={latest?.golden}
                isSpinning={spinTiles[1]}
                testid="reel-golden"
              />
            </div>
          </div>
          <div className="tile" data-testid="tile-aakhri">
            <div className="tile-label"><span className="crown">♛</span> Aakhri Dao</div>
            <div className="tile-value" data-testid="latest-aakhri-value">
              <RollingNumber
                value={latest?.aakhri_dao}
                isSpinning={spinTiles[2]}
                testid="reel-aakhri"
              />
            </div>
          </div>
        </div>

        <h1 className="chart-title" data-testid="chart-title">Daily Result Chart</h1>

        <div className="date-row">
          <span className="label">Select Date</span>
          <label className="date-input-wrap" data-testid="date-input-wrap">
            <span className="date-display" data-testid="date-display">
              {formatDateDMY(selectedDate)}
            </span>
            <Calendar className="cal-icon" size={20} />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              data-testid="date-picker-input"
              aria-label="Select date"
            />
          </label>
          <button className="search-btn" onClick={onSearch} data-testid="search-btn">
            Search
          </button>
        </div>

        <table className="result-table" data-testid="result-table">
          <thead>
            <tr>
              <th>DrawTime</th>
              <th>Jackpot Royal</th>
              <th>Golden</th>
              <th>AakhriDao</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isLatestRow = isTodayView && r.time_slot === latestSlot;
              return (
                <tr
                  key={r.time_slot}
                  data-testid={`row-${r.time_slot}`}
                  className={isLatestRow ? "row-latest" : ""}
                >
                  <td className="time-cell">
                    {formatSlotDisplay(r.time_slot)}
                    {isLatestRow && (
                      <span className="latest-badge" data-testid="latest-badge">
                        LATEST RESULT
                      </span>
                    )}
                  </td>
                  <td className={r.jackpot_regel ? "" : "dash"} data-testid={`cell-jr-${r.time_slot}`}>
                    {displayValue(r.jackpot_regel)}
                  </td>
                  <td className={r.golden ? "" : "dash"} data-testid={`cell-gd-${r.time_slot}`}>
                    {displayValue(r.golden)}
                  </td>
                  <td className={r.aakhri_dao ? "" : "dash"} data-testid={`cell-ad-${r.time_slot}`}>
                    {displayValue(r.aakhri_dao)}
                  </td>
                </tr>
              );
            })}
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={4}>Loading…</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* How to Play / Timing / Kaise Dekhein cards */}
      <HowToPlay />

      {/* Floating WhatsApp share button (mobile-first, also usable on desktop) */}
      <WhatsAppShare latest={latest} dateLabel={formatDateDMY(queryDate)} />

      <div className="admin-footer">
        <div className="site-footer-nav">
          <Link to="/" data-testid="footer-home">Home</Link>
          <span className="dot">.</span>
          <Link to="/about" data-testid="footer-about">About</Link>
          <span className="dot">.</span>
          <Link to="/faq" data-testid="footer-faq">FAQ</Link>
          <span className="dot">.</span>
          <Link to="/privacy" data-testid="footer-privacy">Privacy</Link>
          <span className="dot">.</span>
          <Link to="/disclaimer" data-testid="footer-disclaimer">Disclaimer</Link>
          <span className="dot">.</span>
          <Link to="/contact" data-testid="footer-contact">Contact</Link>
        </div>
        <div className="site-footer-copy">&copy; {new Date().getFullYear()} LuckyWin5G</div>
      </div>
    </div>
  );
}
